import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Form,
  Modal,
  Spinner,
  Table,
  Toast,
  ToastContainer,
} from 'react-bootstrap';

import {
  createMaterialCategory,
  deleteMaterialCategory,
  fetchMaterialCategories,
  updateMaterialCategory,
} from '@/features/automation/videoEditing/services/materialService.js';
import PageContentLayout from '@/components/layout/PageContentLayout/PageContentLayout.js';
import '../materials/VideoMaterialsPage.scss';

const formatTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('zh-CN', { hour12: false });
};

const createEmptyForm = () => ({
  name: '',
  sort_order: 0,
  is_active: true,
});

const VideoMaterialCategoriesPage = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const [form, setForm] = useState(createEmptyForm());
  const [toastInfo, setToastInfo] = useState({
    show: false,
    message: '',
    variant: 'success',
  });

  const showToast = useCallback((message, variant = 'success') => {
    setToastInfo({ show: true, message, variant });
  }, []);

  const loadCategories = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchMaterialCategories({ include_inactive: true });
      setCategories(data);
    } catch (error) {
      showToast(error.response?.data?.message || '获取分类失败', 'danger');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const openCreateModal = () => {
    setEditingCategory(null);
    setForm(createEmptyForm());
    setShowModal(true);
  };

  const openEditModal = (category) => {
    setEditingCategory(category);
    setForm({
      name: category.name || '',
      sort_order: category.sort_order || 0,
      is_active: category.is_active !== false,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
    setEditingCategory(null);
  };

  const handleChange = (event) => {
    const { name, value, checked, type } = event.target;
    setForm((current) => ({
      ...current,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const name = form.name.trim();
    if (!name) {
      showToast('请输入分类名称', 'danger');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name,
        sort_order: Number(form.sort_order) || 0,
        is_active: !!form.is_active,
      };
      if (editingCategory) {
        await updateMaterialCategory(editingCategory.id, payload);
        showToast('分类已更新');
      } else {
        await createMaterialCategory(payload);
        showToast('分类已创建');
      }
      setShowModal(false);
      setEditingCategory(null);
      await loadCategories();
    } catch (error) {
      showToast(error.response?.data?.message || '保存分类失败', 'danger');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (category) => {
    setSaving(true);
    try {
      await updateMaterialCategory(category.id, {
        is_active: !category.is_active,
      });
      await loadCategories();
    } catch (error) {
      showToast(error.response?.data?.message || '更新状态失败', 'danger');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteCandidate) return;
    setSaving(true);
    try {
      await deleteMaterialCategory(deleteCandidate.id);
      setDeleteCandidate(null);
      await loadCategories();
      showToast('分类已删除');
    } catch (error) {
      showToast(error.response?.data?.message || '删除分类失败', 'danger');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <ToastContainer position="top-center" className="p-3">
        <Toast
          show={toastInfo.show}
          onClose={() => setToastInfo({ ...toastInfo, show: false })}
          delay={3000}
          autohide
          bg={toastInfo.variant}
        >
          <Toast.Body
            className={
              toastInfo.variant === 'warning' || toastInfo.variant === 'light'
                ? 'text-dark'
                : 'text-white'
            }
          >
            {toastInfo.message}
          </Toast.Body>
        </Toast>
      </ToastContainer>

      <PageContentLayout
        pageClassName="video-materials-page"
        title="分类管理"
        actions={
          <Button variant="primary" onClick={openCreateModal} disabled={saving}>
            <i className="bi bi-plus-lg" />
            <span>新增分类</span>
          </Button>
        }
        bodyClassName="materials-table-wrap"
      >
        {loading ? (
          <div className="materials-loading">
            <Spinner animation="border" size="sm" />
            <span>加载中</span>
          </div>
        ) : (
          <Table bordered hover responsive className="materials-table">
            <thead>
              <tr>
                <th>分类名称</th>
                <th>排序</th>
                <th>状态</th>
                <th>更新时间</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((category) => (
                <tr key={category.id}>
                  <td>{category.name}</td>
                  <td>{category.sort_order ?? 0}</td>
                  <td>
                    <Form.Check
                      type="switch"
                      id={`material-category-active-${category.id}`}
                      checked={category.is_active !== false}
                      onChange={() => handleToggleActive(category)}
                      disabled={saving}
                    />
                  </td>
                  <td>{formatTime(category.update_time || category.create_time)}</td>
                  <td>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="me-2"
                      onClick={() => openEditModal(category)}
                      disabled={saving}
                    >
                      编辑
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => setDeleteCandidate(category)}
                      disabled={saving}
                    >
                      删除
                    </Button>
                  </td>
                </tr>
              ))}
              {categories.length === 0 && (
                <tr>
                  <td colSpan="5" className="text-center">
                    暂无分类
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        )}
      </PageContentLayout>

      <Modal show={showModal} onHide={closeModal} backdrop={saving ? 'static' : true} centered>
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton={!saving}>
            <Modal.Title>{editingCategory ? '编辑分类' : '新增分类'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>分类名称</Form.Label>
              <Form.Control
                name="name"
                value={form.name}
                onChange={handleChange}
                disabled={saving}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>排序</Form.Label>
              <Form.Control
                name="sort_order"
                type="number"
                value={form.sort_order}
                onChange={handleChange}
                disabled={saving}
              />
            </Form.Group>
            <Form.Check
              type="switch"
              id="material-category-is-active"
              name="is_active"
              label="启用"
              checked={form.is_active}
              onChange={handleChange}
              disabled={saving}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" type="button" onClick={closeModal} disabled={saving}>
              取消
            </Button>
            <Button variant="primary" type="submit" disabled={saving}>
              {saving ? '保存中' : '保存'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal
        show={!!deleteCandidate}
        onHide={() => !saving && setDeleteCandidate(null)}
        backdrop={saving ? 'static' : true}
        centered
      >
        <Modal.Header closeButton={!saving}>
          <Modal.Title>删除分类</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          确认删除“{deleteCandidate?.name || '-'}”？
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setDeleteCandidate(null)}
            disabled={saving}
          >
            取消
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={saving}>
            {saving ? '删除中' : '确认删除'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default VideoMaterialCategoriesPage;
